import { css } from '@emotion/react';
import styled from '@emotion/styled';
import sample from 'lodash/sample';
import { NextPage, NextPageContext } from 'next';
import React from 'react';
import ExclamationSVG from '../../src/assets/svg/exclamation.svg';
import PrimaryButton from '../common-components/component-library/buttons/PrimaryButton';
import Typography from '../common-components/component-library/text/Typography';
import { EVENT_NAME, sendServerEvent } from '../utils/analytics';
import log from '../utils/log';
import { breakpoints, colors, spacing } from '../utils/styleguide';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  background-color: ${colors.yellow};
  min-height: 100vh;
`;

const Exclamation = styled(ExclamationSVG)`
  position: relative;
  top: -5px;
  height: 73px;
  width: 39px;
  ${breakpoints.desktop} {
    top: -10px;
    height: 131px;
    width: 70px;
  }
`;

const userErrorMessages = {
  404: [
    '🐋 Whale hello there, looks like you found the 404 page',
    'Looks like you’re tangled in our web 🕸🕷',
  ],
  anyError: [
    'Well...this is hawkward 🦅',
    'Looks like a monkey got to the cables at Milkywire HQ 🙈',
    '🐛 As much as we like bugs 🐞 you’d probably rather go:',
    '🐫 Don’t get the hump, just press the button to go:',
  ],
};

export function generateErrorMessage(statusCode?: number | string): string {
  return sample(
    statusCode == 404
      ? userErrorMessages.anyError.concat(userErrorMessages[404])
      : userErrorMessages.anyError,
  ) as string;
}

type Props = {
  statusCode?: string | number;
};

const ErrorPage: NextPage<Props> = ({ statusCode }) => {
  const message = generateErrorMessage(statusCode);

  return (
    <Layout>
      <div
        css={css`
          max-width: 740px;
          padding: 16px;
        `}
      >
        <span
          css={css`
            display: flex;
          `}
        >
          <Typography useBuiltInMargins variant="scribbleDisplay">
            {statusCode === 404 ? statusCode : `${statusCode} error`}
          </Typography>
          <Exclamation />
        </span>
        <Typography
          useBuiltInMargins
          variant="titleH4"
          css={css`
            margin-top: ${spacing[3]}px;
            margin-bottom: ${spacing[5]}px;
            ${breakpoints.desktop} {
              margin-top: ${spacing[6]}px;
              margin-bottom: ${spacing[10]}px;
            }
          `}
        >
          {message}
        </Typography>
        <PrimaryButton href="/" color="onYellow">
          Back to Home
        </PrimaryButton>
      </div>
    </Layout>
  );
};

ErrorPage.getInitialProps = (ctx: NextPageContext) => {
  let statusCode = 404;
  if (ctx.res) {
    statusCode = ctx.res.statusCode;
  } else if (ctx.err && ctx.err.statusCode) {
    statusCode = ctx.err.statusCode;
  }

  sendServerEvent({
    name: EVENT_NAME.HTTP_NOT_OK,
    properties: {
      statusCode,
      statusDescription: statusCode === 404 ? 'not found' : undefined,
      url: ctx.req ? `https://www.milkywire.com${ctx.req.url}` : undefined,
    },
  });

  log.error({ err: ctx.err, statusCode }, 'Error could not serve page');
  return { statusCode };
};

export default ErrorPage;
